import React, {useContext} from "react";
import {
    Paper,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel
} from "@mui/material";
import i18n from "../../../../i18n/i18n";
import "./table.scss";
import {SortDirection} from "../../../models/table";
import {storesContext} from "../../../stores/stores";
import Loader from "../loader/loader";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {observer} from "mobx-react";

export type TableActionFunction = (model: any) => JSX.Element;

interface TableProps {
    headers: string[];
    rows: any[];
    properties: string[];
    testId: string;
    actions?: TableActionFunction;
    actionsLabel?: string;
    renderCell?: (row: any, property: string) => JSX.Element;
    pageable?: boolean | false;
    renderEmptyCell?: boolean | false;
    sortableElements?: string[];
    sortingAction?: (orderBy: string, sortDirection: SortDirection) => void;
    orderBy?: string;
    sortDirection?: SortDirection;
    currentPage: number;
    numberOfElements?: number;
    onPageChange?: (event: any, offset: number) => void;
    isOldPagination?: boolean; // Variable to be removed after introducing new paging from BE to all tables
    isTableLoader?: boolean;
}

function Table(props: TableProps): JSX.Element {
    const paginationStore = useContext(storesContext).paginationStore;
    const [page, setPage] = React.useState(props.currentPage);
    const [rowsPerPage, setRowsPerPage] = React.useState(paginationStore.size);
    const [order, setOrder] = React.useState<SortDirection>(props.sortDirection || SortDirection.asc);
    const [orderBy, setOrderBy] = React.useState<undefined | string>(props.orderBy || undefined);
    const headers = props.actions ? props.headers.concat("") : props.headers;
    let rowsInPage = 0;

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        if (!props.isOldPagination && props.onPageChange) {
            const offset = paginationStore.size * newPage;
            paginationStore.setOffset(offset);
            props.onPageChange(event, offset);
        }
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const newSize = parseInt(event.target.value, 10);
        paginationStore.clearPagination();
        paginationStore.setSize(newSize);

        if (props.isOldPagination) {
            setRowsPerPage(paginationStore.size); // To be removed after introducing new paging from BE to all tables
        } else if (props.onPageChange) {
            props.onPageChange(event, paginationStore.offset);
        }
        setPage(0);
    };

    const isSortableHeader = (property: string) => {
        return props.sortableElements && props.sortableElements.length && props.sortableElements.length > 0 &&
            props.sortableElements.includes(property) && props.sortingAction;
    };

    return (
        <TableContainer className="table" component={Paper}>
            <MuiTable data-testid={props.testId}>
                <TableHead className="table-header" data-testid={`${props.testId}-header`}>
                    <TableRow>
                        {headers.map((header, index) => {
                            const property = props.properties[index];

                            if (isSortableHeader(property)) {
                                return renderHeaderSortable(header, index, property);
                            } else if (props.actionsLabel && index > props.properties.length -1) {
                                return renderHeaderDefault(props.actionsLabel, index);
                            } else {
                                return renderHeaderDefault(header, index);
                            }
                        })}
                    </TableRow>
                </TableHead>
                <TableBody className="table-body" data-testid={`${props.testId}-body`}>
                    {props.isTableLoader ? renderLoader() : (
                        props.isOldPagination ?
                            props.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => renderRow(props, row, index)) :
                            props.rows.map((row, index) => renderRow(props, row, index))
                    )}
                </TableBody>
                <TableFooter>
                    {props.pageable && renderPaginationToolbar()}
                </TableFooter>
            </MuiTable>
        </TableContainer>
    );

    function renderHeaderDefault(header: string, index: number) {
        return (
            <TableCell key={`header-${index}`} align="left">
                {header}
            </TableCell>
        );
    }

    function renderHeaderSortable(header: string, index: number, property: string) {
        const isActive = orderBy === property;

        return (
            <TableCell key={`header-${index}`} align="left" sortDirection={orderBy === property ? order : false}>
                <TableSortLabel
                    active={isActive}
                    direction={orderBy === property ? order : undefined}
                    hideSortIcon={true}    // hide sort icon on mouse hover
                    onClick={() => sortHandler(property)}
                >
                    {header}
                    {isActive ? null : <UnfoldMoreIcon className="sortable-icon"/>}
                </TableSortLabel>
            </TableCell>
        );
    }

    function renderLoader() {
        return (
            <TableRow>
                <TableCell>
                    <Loader />
                </TableCell>
            </TableRow>
        );
    }

    function renderRow(props: TableProps, row: any, index: number) {
        const tableRow: JSX.Element[] = [];

        props.properties.map(((property, i) => {
            let renderCellResult: JSX.Element = <></>;

            if (props && row && property && props.renderCell) {
                renderCellResult = renderCell(props, row, property);
            }

            if (Object.prototype.hasOwnProperty.call(row, property) || props.renderEmptyCell || renderCellResult?.props?.children) {
                tableRow.push(
                    <TableCell
                        key={`cell-${index}-${i}`}
                        align="left">
                        {renderCell(props, row, property)}
                    </TableCell>);
            } else {
                tableRow.push(<TableCell key={`cell-${index}-${i}`} align="left" className="no-data">???</TableCell>);
            }
        }));

        renderActions(tableRow, index, row);

        return (
            <TableRow
                key={`row-${index}`}
                sx={{"&:last-child td, &:last-child th": {border: 0}}}
                data-testid={`${props.testId}-row-${index}`}
            >
                {tableRow}
            </TableRow>
        );
    }

    function renderCell(props: TableProps, row: any, property: string): JSX.Element {
        if (props.renderCell) {
            return props.renderCell(row, property);
        }
        return row[property];
    }

    function renderActions(tableRow: JSX.Element[], index: number, model: any) {
        if (!props.actions) {
            return;
        }

        tableRow.push(
            <TableCell align="right" key={`action-${index}`} className="actions">
                {props.actions(model)}
            </TableCell>
        );
    }

    function renderPaginationToolbar() {
        const numberOfElements = props.isOldPagination ? props.rows.length : (
            paginationStore.numberOfElements ? paginationStore.numberOfElements : props.numberOfElements);

        return (
            <tr>
                <TablePagination
                    page={page}
                    count={numberOfElements || 0}
                    onPageChange={handleChangePage}
                    rowsPerPage={props.isOldPagination ? rowsPerPage : paginationStore.size}
                    rowsPerPageOptions={[5,10,20,50]}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={i18n.t("pageable.rowsPerPageLabel")}
                    labelDisplayedRows={defaultLabelDisplayedRows}
                    component="td"
                    showFirstButton
                    showLastButton
                    getItemAriaLabel={(typeLabel: string) => getLabelFromPaginationButton(typeLabel)}
                />
            </tr>
        );
    }

    function getLabelFromPaginationButton(type: string) {
        switch (type) {
            case "next":
                return i18n.t("pageable.arriaLabel", {type: i18n.t("pageable.arialLabelNext")});
            case "previous":
                return i18n.t("pageable.arriaLabel", {type: i18n.t("pageable.arialLabelBack")});
            case "first":
                return i18n.t("pageable.arriaLabel", {type: i18n.t("pageable.arialLabelFirst")});
            case "last":
                return i18n.t("pageable.arriaLabel", {type: i18n.t("pageable.arialLabelLast")});
            default:
                return "";
        }
    }

    function defaultLabelDisplayedRows() {
        let numberOfPages = 0;
        let from = 0;
        let numberOfElements = 0;

        if (props.isOldPagination) { // To be removed after introducing new paging from BE to all tables
            numberOfPages = Math.floor(props.rows.length / rowsPerPage);
            from = props.rows.length > 0 ? page * rowsPerPage + 1 : page * rowsPerPage;
            numberOfElements = props.rows.length;

            if (page === numberOfPages) {
                rowsInPage = numberOfElements;
            } else if (props.rows.length === 0) {
                rowsInPage = 0;
            } else {
                rowsInPage = page * rowsPerPage + rowsPerPage;
            }
        } else {
            numberOfPages = paginationStore.numberOfPages;
            from = props.rows.length > 0 ? page * paginationStore.size + 1 : page * paginationStore.size;
            numberOfElements = paginationStore.numberOfElements;

            rowsInPage = paginationStore.size * page + paginationStore.size;

            if (page === numberOfPages - 1) {
                rowsInPage = numberOfElements;
            } else if (numberOfElements === 0) {
                rowsInPage = 0;
            } else if (paginationStore.size > numberOfElements) {
                rowsInPage = numberOfElements;
            }
        }

        return `${from}–${rowsInPage} ${i18n.t("pageable.labelDisplayedRows")} ${numberOfElements}`;
    }

    function sortHandler(header: string) {
        if (!props.sortingAction) {
            return;
        }

        let newOrder: SortDirection;

        if (header !== orderBy) {
            newOrder = SortDirection.asc;
        } else {
            newOrder = order === SortDirection.asc ? SortDirection.desc : SortDirection.asc;
        }

        setOrder(newOrder);
        setOrderBy(header);

        props.sortingAction(header, newOrder);
    }
}

export default observer(Table);
